/**
 * NOTE: For compatibility across platforms all calls **must** return a
 * `Promise` object!
 */

import { PlatformMessage } from '@reachmedianetwork/post-message-client-rds-api';
import { PlayerPmc } from '../PlayerPmc';

/**
 * Successful result of {@link RootStorageFilesInterface.getRootStorageFile}
 */
export type GetRSFilePromiseSuccess = {
    /** base64 encoded string of file content */
    fiString: string;
};

/**
 * Failed result of {@link RootStorageFilesInterface} Promises
 */
export type RSPromiseFailure = Error;

/**
 * @example
 * ```json
 * {
 *     fileName: "MyFile.txt",
 *     fileExtension: ".txt",
 *     fullPath: "C:\\Documents\MyFile.txt"
 * }
 * ```
 */
type FileInfo = {
    /** The name of the file */
    fileName: string;
    /** The file's extension */
    fileExtension: string;
    /** The full path to the file's location */
    fullPath: string;
};

/**
 * Successful result of {@link RootStorageFilesInterface.getListOfRootStorageFiles}
 */
export type ListRSFilesPromiseSuccess = {
    /**
     * An array of information objects describing the files in root storage
     * directory
     */
    diFiles: Array<FileInfo>;
};

export interface RootStorageFilesInterface {
    /**
     * **Feature:** Storage Files -- Get a file from the player's storage folder.
     * @param fiName - The name of the file to be retrieved
     * @throws {@link RSPromiseFailure}
     */
    getRootStorageFile(fiName: string): PromiseLike<GetRSFilePromiseSuccess>;

    /**
     * **Feature:** Storage Files -- Get a list of files in the player's storage
     * folder.
     * @throws {@link RSPromiseFailure}
     */
    getListOfRootStorageFiles(): PromiseLike<ListRSFilesPromiseSuccess>;
}

export function supportsRootStorageFiles(platform: unknown): platform is RootStorageFilesInterface {
    if (platform instanceof PlayerPmc) {
        return (
            platform.supportsMessage(PlatformMessage.RootStorageFileGet) &&
            platform.supportsMessage(PlatformMessage.RootStorageFileList)
        );
    }
    return (
        platform instanceof Object &&
        'getRootStorageFile' in platform &&
        'getListOfRootStorageFiles' in platform
    );
}
